import {
  ghostBtnClass,
  mfgInlineInputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  summarizeMaterialsEffect,
  type NamedVariantMaterialsEffect,
} from "@/lib/manufacturing/namedVariantMaterialsEffect";
import type { NamedVariantApplicationSummary } from "@/lib/manufacturing/namedVariantsClient";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import { ManufacturingNamedVariantApplicationEffectEditor } from "./ManufacturingNamedVariantApplicationEffectEditor";
import { ManufacturingNamedVariantApplicationImpact } from "./ManufacturingNamedVariantApplicationImpact";

export function ManufacturingNamedVariantApplicationRow({
  app,
  expanded,
  selected,
  selectable,
  showEffectEditor,
  templateMode,
  materials,
  orgSlotLabels,
  saveBusy,
  previewBusy,
  onToggleExpanded,
  onToggleSelected,
  onPreview,
  onConfirm,
  onDelete,
  onUpdatePrices,
  onUpdateEffect,
  onToggleEditEffect,
}: {
  app: NamedVariantApplicationSummary;
  expanded: boolean;
  selected: boolean;
  selectable: boolean;
  showEffectEditor: boolean;
  templateMode: NamedVariantMaterialsEffect["mode"];
  materials: readonly MfgMaterial[];
  orgSlotLabels: readonly string[];
  saveBusy: boolean;
  previewBusy: boolean;
  onToggleExpanded: () => void;
  onToggleSelected: () => void;
  onPreview: () => void;
  onConfirm: () => void;
  onDelete: () => void;
  onUpdatePrices: (patch: {
    wholesale_price_delta?: number | null;
    retail_price_delta?: number | null;
    included_by_default?: boolean;
  }) => void;
  onUpdateEffect: (materials_effect: NamedVariantMaterialsEffect) => void;
  onToggleEditEffect: () => void;
}) {
  const label = `${app.parent_recipe_code} — ${app.parent_recipe_name}`;
  return (
    <li className="rounded-xl border border-gray-200 dark:border-zinc-700">
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          {selectable ? (
            <input
              type="checkbox"
              className="mt-1"
              checked={selected}
              aria-label={`Select ${label}`}
              onChange={onToggleSelected}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span className="mt-1 inline-block w-4 shrink-0" aria-hidden />
          )}
          <button
            type="button"
            className="min-w-0 flex-1 text-left text-sm"
            aria-expanded={expanded}
            onClick={onToggleExpanded}
          >
            <span className="font-semibold text-gray-900 dark:text-zinc-100">{label}</span>
            {app.confirm_status === "pending" ? (
              <span
                className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-bold text-amber-900 dark:bg-amber-950 dark:text-amber-100"
                title="Pending confirmation"
              >
                !
              </span>
            ) : null}
            <span className="ml-2 text-[11px] text-gray-400 dark:text-zinc-500">
              {expanded ? "Hide details" : "Details"}
            </span>
          </button>
        </div>
        <div className="flex flex-wrap gap-1">
          <button
            type="button"
            className={ghostBtnClass}
            disabled={previewBusy}
            onClick={onPreview}
          >
            Preview
          </button>
          {app.confirm_status === "pending" ? (
            <button
              type="button"
              className={mfgPrimaryBtnClass}
              disabled={saveBusy}
              onClick={onConfirm}
            >
              Confirm
            </button>
          ) : null}
          <button
            type="button"
            className={ghostBtnClass}
            disabled={saveBusy}
            onClick={onDelete}
          >
            Remove
          </button>
        </div>
      </div>

      {expanded ? (
        <div className="space-y-2 border-t border-gray-100 px-3 py-2 dark:border-zinc-800">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs text-gray-500 dark:text-zinc-400">
              {summarizeMaterialsEffect(app.materials_effect)}
              {app.ai_rationale ? ` — ${app.ai_rationale}` : ""}
            </div>
            {app.confirm_status === "confirmed" ? (
              <button type="button" className={ghostBtnClass} onClick={onToggleEditEffect}>
                {showEffectEditor ? "Hide materials editor" : "Edit materials effect"}
              </button>
            ) : null}
          </div>
          {showEffectEditor ? (
            <ManufacturingNamedVariantApplicationEffectEditor
              key={`${app.id}-${JSON.stringify(app.materials_effect)}-${templateMode}`}
              effect={app.materials_effect}
              templateMode={templateMode}
              parentRecipeId={app.parent_recipe_id}
              materials={materials}
              orgSlotLabels={orgSlotLabels}
              saveBusy={saveBusy}
              onSave={onUpdateEffect}
              namedVariantId={app.named_variant_id}
            />
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs">
              Wholesale $
              <input
                className={`${mfgInlineInputClass} ml-1 max-w-[90px]`}
                type="number"
                defaultValue={app.wholesale_price_delta ?? ""}
                onBlur={(e) => {
                  const n = e.target.value === "" ? null : Number(e.target.value);
                  onUpdatePrices({
                    wholesale_price_delta:
                      n == null || Number.isFinite(n) ? n : app.wholesale_price_delta,
                  });
                }}
              />
            </label>
            <label className="text-xs">
              Retail $
              <input
                className={`${mfgInlineInputClass} ml-1 max-w-[90px]`}
                type="number"
                defaultValue={app.retail_price_delta ?? ""}
                onBlur={(e) => {
                  const n = e.target.value === "" ? null : Number(e.target.value);
                  onUpdatePrices({
                    retail_price_delta:
                      n == null || Number.isFinite(n) ? n : app.retail_price_delta,
                  });
                }}
              />
            </label>
            <label className="flex items-center gap-1 text-xs">
              <input
                type="checkbox"
                checked={app.included_by_default}
                onChange={(e) => onUpdatePrices({ included_by_default: e.target.checked })}
              />
              Include by default on deals
            </label>
          </div>
          <ManufacturingNamedVariantApplicationImpact
            applicationId={app.id}
            effectKey={JSON.stringify(app.materials_effect)}
          />
        </div>
      ) : null}
    </li>
  );
}
